import { AxiosResponse } from 'axios';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import fetcher, {
    fetcherFull,
    MethodEnum,
} from 'utils/helpers/browser/newFetcher';
import {
    ClubCreateApiInterface,
    ClubPersonCreateApiInterface,
    UploadPhotoApiInterface,
    ClubSignatureCreateUpdatePayload,
} from 'submodules/api_middleware';
import {
    ClubPersonAdminCreateApiInterface,
    ClubPitchUpdatePayload,
    ExportApiType,
    TransactionListApiInterface,
} from 'submodules/api_middleware/src/interfaces/ApiInterface';
import {
    ClubPayloadInterface,
    ClubRepresentativePayloadInterface,
} from 'components/interfaces/ClubInterface';
import { FormResponseData } from 'services/user.service';
import { QueryRequestInterface } from 'submodules/api_middleware/src/interfaces/InternalInterfaces';

export const createClub = async (
    data: ClubCreateApiInterface
): Promise<AxiosResponse> => {
    return await fetcher(`/api/club/create`, MethodEnum.POST, undefined, data);
};

export const getClubList = async (
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcher(`/api/club/list`, MethodEnum.GET, query);
};

export const getClubListAdminOnly = async (
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcher(`/api/club/adminList`, MethodEnum.GET, query);
};

export const uploadPhotoClub = async (
    data: UploadPhotoApiInterface
): Promise<FormResponseData> => {
    return await fetcher('/api/club/uploadPhoto', MethodEnum.POST, undefined, {
        data,
    });
};

export const getClubFullList = async (
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcherFull(`/api/club/list`, MethodEnum.GET, query);
};

export const getClubPlayersFullList = async (
    clubId: number,
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcherFull(
        `/api/club/${clubId}/players/list`,
        MethodEnum.GET,
        query
    );
};

export const getClubPersonsFullList = async (
    clubId: number,
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcherFull(
        `/api/club/${clubId}/persons/list`,
        MethodEnum.GET,
        query
    );
};

export const getClubCompetitionTeamsFullList = async (
    clubId: number,
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return await fetcherFull(
        `/api/club/${clubId}/competition_teams/list`,
        MethodEnum.GET,
        query
    );
};

export const clubPersonCreate = async (
    id: number,
    data: ClubPersonCreateApiInterface
): Promise<AxiosResponse> => {
    return await fetcher(
        `/api/club/${id}/persons/create`,
        MethodEnum.POST,
        undefined,
        data
    );
};

export const clubPersonAdminCreate = async (
    id: number,
    data: ClubPersonAdminCreateApiInterface
): Promise<AxiosResponse> => {
    return await fetcher(
        `/api/club/${id}/persons/admin_create`,
        MethodEnum.POST,
        undefined,
        data
    );
};

export const clubPersonDelete = async (
    id: number,
    personId: number
): Promise<FormResponseData> => {
    return await fetcher(
        `/api/club/${id}/persons/${personId}/delete`,
        MethodEnum.POST
    );
};

export const clubWorkerCreate = async (
    id: number,
    data: ClubRepresentativePayloadInterface
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${id}/workers/create`,
        MethodEnum.POST,
        undefined,
        { data }
    );
};

export const clubWorkerUpdate = async (
    id: number,
    workerId: string,
    data: ClubRepresentativePayloadInterface
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${id}/workers/${workerId}/update`,
        MethodEnum.PUT,
        undefined,
        { data }
    );
};

export const clubUpdate = async (
    id: number,
    data: ClubPayloadInterface
): Promise<AxiosResponse> => {
    return fetcher(`/api/club/${id}/update`, MethodEnum.PUT, undefined, {
        data,
    });
};

export const getClubPersonsWithoutMembership = async (
    id: number,
    query: NextApiRequestQuery
): Promise<AxiosResponse> => {
    return fetcherFull(
        `/api/club/${id}/persons/persons_without_membership`,
        MethodEnum.GET,
        query
    );
};

export const createClubBulkMembership = async (
    id: number,
    personIds: number[]
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${id}/bulk_memberships/create`,
        MethodEnum.POST,
        undefined,
        {
            personIds,
        }
    );
};

export const getClubDetails = async (id: number): Promise<AxiosResponse> => {
    return fetcher(`/api/club/${id}`, MethodEnum.GET);
};

export const getClubWorkers = async (id: number): Promise<AxiosResponse> => {
    return fetcher(`/api/club/${id}/workers`, MethodEnum.GET);
};

export const addClubPitch = async (
    clubId: number,
    data: ClubPitchUpdatePayload
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${clubId}/pitch/create`,
        MethodEnum.POST,
        undefined,
        data
    );
};

export const editClubPitch = async (
    clubId: number,
    pitchId: number,
    data: ClubPitchUpdatePayload
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${clubId}/pitch/${pitchId}/update`,
        MethodEnum.PUT,
        undefined,
        data
    );
};

export const getClubListAccountTransactions = async (
    clubId: number,
    query: QueryRequestInterface
): Promise<AxiosResponse<TransactionListApiInterface[] | ExportApiType>> => {
    return await fetcher(
        `/api/club/${clubId}/account/transactions`,
        MethodEnum.GET,
        query
    );
};

export const addClubSignature = async (
    clubId: number,
    data: ClubSignatureCreateUpdatePayload
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${clubId}/signature/create`,
        MethodEnum.POST,
        undefined,
        data
    );
};

export const editClubSignature = async (
    clubId: number,
    signatureId: number,
    data: ClubSignatureCreateUpdatePayload
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${clubId}/signature/${signatureId}/update`,
        MethodEnum.PUT,
        undefined,
        data
    );
};

export const deleteClubSignature = async (
    clubId: number,
    signatureId: number
): Promise<AxiosResponse> => {
    return fetcher(
        `/api/club/${clubId}/signature/${signatureId}/delete`,
        MethodEnum.DELETE
    );
};
